/**
 * Token storage.
 *
 * localStorage, deliberately: the API is token-based rather than
 * cookie-based, and contributors record on their own phones where losing the
 * session on every reload would be miserable. Nothing but the token pair is
 * kept here — profile data is always re-fetched from /auth/me, so a stale
 * cache can never show someone the wrong balance.
 */
const KEY = "speedradio.contributor.session";

export interface Session {
  access_token: string;
  refresh_token: string;
}

export function readSession(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    return parsed?.access_token ? parsed : null;
  } catch {
    return null;
  }
}

export function writeSession(session: Session): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    /* private browsing, quota — the app still works for this session */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** The reviewer console's credentials, kept apart from contributor tokens. */
const REVIEW_KEY = "speedradio.contributor.review";

export interface ReviewCredentials {
  mode: "token" | "adminKey";
  value: string;
}

export function readReviewCredentials(): ReviewCredentials | null {
  try {
    const raw = sessionStorage.getItem(REVIEW_KEY);
    return raw ? (JSON.parse(raw) as ReviewCredentials) : null;
  } catch {
    return null;
  }
}

export function writeReviewCredentials(creds: ReviewCredentials): void {
  try {
    // sessionStorage, not local: a shared ops machine should not keep an
    // admin key around after the tab closes.
    sessionStorage.setItem(REVIEW_KEY, JSON.stringify(creds));
  } catch {
    /* ignore */
  }
}

export function clearReviewCredentials(): void {
  try {
    sessionStorage.removeItem(REVIEW_KEY);
  } catch {
    /* ignore */
  }
}
